import { useState, useMemo, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FiEdit2, FiSave, FiX, FiLink } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import LeadFieldMapModal from "./LeadFieldMapModal";
import LeadStatusBadge from "../../leads/components/LeadStatusBadge";
import DynamicLeadForm from "../../leads/components/DynamicLeadForm";
import { mapLeadDataToFormValue } from "../../../utils/leadFormUtils";
import StatusDropdown from "../../leads/components/StatusDropdown";
import { toast } from "react-hot-toast";
import apiClient from "../../../api/client";
import {
  useGetLeadFormConfig,
  useGetLeadStatuses,
  useUpdateLead,
  useAddLeadNote,
  useUploadLeadAttachment,
} from "../../leads/api";
import { useProjectDetails } from "../../../api/hooks";
import { getDueDateColor } from "../../../utils/workingDayUtils";

const SectionCard = ({ children }) => (
  <div className="bg-white rounded-xl border border-slate-200  p-4 lg:p-6">
    {children}
  </div>
);

const LabelValue = ({ label, value, isEditing = false, onEdit, valueClassName = "" }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
    {isEditing && onEdit ? (
      <button
        onClick={onEdit}
        className="text-sm font-semibold text-slate-900 hover:text-[#3f8cff] flex items-center gap-1"
      >
        {value || "—"}
        <FiEdit2 size={12} />
      </button>
    ) : (
      <p className={`text-sm font-semibold ${valueClassName || "text-slate-900"}`}>{value || "—"}</p>
    )}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2 text-sm text-slate-600">
    {items.map((item, index) => (
      <li key={`${item}-${index}`} className="flex items-start gap-2">
        <span className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5" />
        {item}
      </li>
    ))}
  </ul>
);

const LeadOverviewSection = ({ lead, isClient = false }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [quickNoteText, setQuickNoteText] = useState("");
  const [mappingModal, setMappingModal] = useState({
    isOpen: false,
    facebookField: "",
    facebookValue: ""
  });

  const { mutate: addNote, isLoading: isAddingNote } = useAddLeadNote();
  const { mutateAsync: uploadAttachment, isLoading: isUploadingFile } = useUploadLeadAttachment();

  const handleSaveQuickNote = () => {
    if (!quickNoteText.trim()) return;
    addNote(
      {
        leadId: lead.id || lead._id,
        noteData: { text: quickNoteText.trim() },
      },
      {
        onSuccess: () => {
          toast.success("Note added successfully");
          setQuickNoteText("");
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to add note");
        },
      }
    );
  };

  const handleQuickFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadAttachment({ leadId: lead.id || lead._id, file });
      toast.success("File uploaded successfully");
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to upload file");
    }
  };
  // Get contact from both possible locations
  const contact = lead.contact || lead.details?.contact;

  const leadDetails = lead.details?.leadDetails || {};
  // Get AI suggestions from lead data (backwards compatible)
  const aiSuggestions = lead.aiSuggestions || lead.details?.aiSuggestions;

  // Debug: Log lead structure to understand data flow
  useEffect(() => {
    if (lead) {
      console.log("LeadOverviewSection - Lead data:", {
        "lead.contact": lead.contact,
        "lead.details?.contact": lead.details?.contact,
        "contact (extracted)": contact,
        "lead.status": lead.status,
        "lead.source": lead.source,
        "lead.customFields": lead.customFields,
      });
    }
  }, [lead, contact]);
  const projectId = typeof lead.project === "object" ? lead.project?._id : lead.project;
  const { data: projectDetails } = useProjectDetails(projectId);
  
  const branches = useMemo(() => {
    // Only branches configured in project Settings → Branch
    const rawBranchLogins = projectDetails?.customFields?.branchLogins || [];
    return rawBranchLogins.map(b => b.name || b.branch).filter(Boolean);
  }, [projectDetails]);

  const { data: formConfigData, isLoading: isLoadingFormConfig } =
    useGetLeadFormConfig(projectId);
  const { data: statusesData, isLoading: isLoadingStatuses } =
    useGetLeadStatuses(projectId);

  const { mutate: updateLead, isLoading: isUpdating } = useUpdateLead();

  // Handle status change from dropdown
  const handleStatusChange = useCallback(
    (statusId) => {
      updateLead(
        {
          leadId: lead.id || lead._id,
          leadData: { status: statusId },
        },
        {
          onSuccess: () => {
            toast.success("Lead status updated successfully");
          },
          onError: (error) => {
            toast.error(
              error?.response?.data?.message || "Failed to update status"
            );
          },
        }
      );
    },
    [lead, updateLead]
  );

  // Stable handler for form value changes (same pattern as AddLeadModal)
  const handleFormValueChange = useCallback((updater) => {
    setFormValues(updater);
  }, []);

  // Default AI suggestions if not provided
  const defaultAiSuggestions = {
    conversationCoach: {
      sentiment: "Neutral",
      recommendations: ["No recommendations available at this time."],
    },
    dealStage: {
      stage: "Initial Contact",
      steps: ["No guidance available at this time."],
    },
  };

  const safeAiSuggestions = aiSuggestions || defaultAiSuggestions;

  // Get form fields - use same pattern as AddLeadModal
  const formFields = useMemo(() => {
    const fields = formConfigData?.data?.fields || [];
    let processedFields = [];
    
    if (fields.length === 0) {
      // Default fields if form config is empty
      processedFields = [
        {
          id: "name",
          key: "system_name",
          label: "Full Name",
          type: "text",
          required: true,
          placeholder: "Enter full name",
        },
        {
          id: "email",
          key: "system_email",
          label: "Email",
          type: "email",
          required: true,
          placeholder: "Enter email address",
        },
        {
          id: "phone",
          key: "system_phone",
          label: "Phone Number",
          type: "tel",
          required: false,
          placeholder: "Enter phone number",
        },
        {
          id: "company",
          key: "company",
          label: "Company",
          type: "text",
          required: false,
          placeholder: "Enter company name",
        },
        {
          id: "status",
          key: "status",
          label: "Lead Status",
          type: "select",
          required: true,
          options: [],
        },
        {
          id: "source",
          key: "source",
          label: "Lead Source",
          type: "select",
          required: false,
          options: ["Manual", "Website", "Import", "Facebook", "WhatsApp", "Instagram", "Other"],
        },
      ];
    } else {
      // Ensure all fields have unique IDs and keys
      processedFields = fields.map((field, index) => {
        const id = field.id || field._id?.toString?.() || field._id || `field_${index}`;
        return {
          ...field,
          id: String(id),
          key: field.key || id,
        };
      });
      
      // Always ensure source field exists
      if (!processedFields.some(f => f.key === "source")) {
        processedFields.push({
          id: "source",
          key: "source",
          label: "Lead Source",
          type: "select",
          required: false,
          options: ["Manual", "Website", "Import", "Facebook", "WhatsApp", "Instagram", "Other"],
        });
      }
    }

    // Add branch field dynamically
    if (!processedFields.some(f => f.key === "branch")) {
      const branchOptions = branches && branches.length > 0 ? ["unknown", ...branches] : [];
      processedFields.push({
        id: "branch",
        key: "branch",
        label: "Branch",
        type: branchOptions.length > 0 ? "select" : "text",
        required: false,
        options: branchOptions.length > 0 ? branchOptions : undefined,
        placeholder: "Enter branch"
      });
    }

    // Add created date field dynamically
    if (!processedFields.some(f => f.key === "fb_created_time")) {
      processedFields.push({
        id: "fb_created_time",
        key: "fb_created_time",
        label: "Created Date",
        type: "date",
        required: false,
        placeholder: "Select created date"
      });
    }
    
    return processedFields;
  }, [formConfigData?.data?.fields, branches]);

  const statuses = useMemo(() => statusesData?.data || [], [statusesData]);

  // Initialize form values from lead data when entering edit mode
  useEffect(() => {
    if (isEditing && formFields.length > 0) {
      const initialValues = {};

      // Map all fields from form config to lead data
      formFields.forEach((field) => {
        const fieldId = String(field.id); // Use field.id, not field.label!
        let value = mapLeadDataToFormValue(field, lead, contact);

        // Format date fields for HTML date input (YYYY-MM-DD)
        if (field.type === "date" && value) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              // Format as YYYY-MM-DD for HTML date input
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              value = `${year}-${month}-${day}`;
            }
          } catch (e) {
            // If parsing fails, use the value as is
            value = String(value);
          }
        }

        // Set the value using field.id as the key (empty string if not found)
        initialValues[fieldId] =
          value !== null && value !== undefined ? String(value) : "";
      });

      setFormValues(initialValues);
      setErrors({});

      // Debug log
      console.log("Edit mode initialized:", {
        formFields: formFields.length,
        formFieldsData: formFields,
        initialValues,
        lead,
        contact,
        "lead.contact": lead.contact,
        customFields: lead.customFields,
      });
    } else if (!isEditing) {
      // Clear form values when exiting edit mode
      setFormValues(prev => Object.keys(prev).length === 0 ? prev : {});
      setErrors(prev => Object.keys(prev).length === 0 ? prev : {});
    }
  }, [isEditing, formFields, lead, contact]);

  const handleSave = () => {
    // Validate form
    const newErrors = {};
    formFields.forEach((field) => {
      if (field.required) {
        const value = formValues[field.id];
        if (!value || (field.type === "checkbox" && !value)) {
          newErrors[field.id] = `${field.label} is required`;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Smart mapping based on keys
    const contactData = {
      name: "",
      email: "",
      phone: "",
      company: "",
    };

    const customFields = {};
    if (lead.customFields instanceof Map) {
      for (let [k, v] of lead.customFields) {
        customFields[k] = v;
      }
    } else if (lead.customFields) {
      Object.assign(customFields, lead.customFields);
    }
    
    let statusId = "";
    let sourceValue = lead.source || "";
    let branchValue = lead.branch || "";

    formFields.forEach((field) => {
      const fieldId = String(field.id);
      const fieldKey = field.key || fieldId;
      const fieldValue = formValues[fieldId];

      if (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === ""
      ) {
        return; // Skip empty
      }

      const stringValue = String(fieldValue).trim();
      if (!stringValue) return;

      // 1. System Fields
      if (fieldKey === "system_name") {
        contactData.name = stringValue;
        return;
      }
      if (fieldKey === "system_email") {
        contactData.email = stringValue;
        return;
      }
      if (fieldKey === "system_phone") {
        contactData.phone = stringValue;
        return;
      }

      // 2. Common Fields (Company)
      if (fieldKey === "company" || field.label?.toLowerCase() === "company") {
        contactData.company = stringValue;
        // Also save to custom fields if it's truly a custom field
        if (fieldKey !== "company") customFields[fieldKey] = fieldValue;
        return;
      }

      // 3. Status
      if (fieldKey === "status") {
        statusId = stringValue;
        return;
      }

      // 4. Source
      if (fieldKey === "source") {
        sourceValue = stringValue;
        return;
      }

      // 5. Branch
      if (fieldKey === "branch") {
        branchValue = stringValue;
        customFields[fieldKey] = fieldValue;
        return;
      }

      // 6. Custom Fields
      customFields[fieldKey] = fieldValue;
    });

    const updateData = {
      contact: contactData,
      status: statusId || lead.status?._id || lead.status?.id || lead.status,
      source: sourceValue,
      branch: branchValue,
      customFields, // Keyed by stable 'key'
    };

    updateLead(
      {
        leadId: lead.id || lead._id,
        leadData: updateData,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          setFormValues({});
          setErrors({});
        },
        onError: (error) => {
          console.error("Error updating lead:", error);
          setErrors({
            submit:
              error.response?.data?.message ||
              "Failed to update lead. Please try again.",
          });
        },
      }
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormValues({});
    setErrors({});
  };

  // Get all field values for display - check both lead.contact and lead.details.contact
  const getFieldValue = (field) => {
    const fieldId = String(field.id);
    const fieldKey = field.key || fieldId;

    // 1. System Fields
    if (fieldKey === "system_name") {
      return contact?.name || lead.contact?.name || "";
    }
    if (fieldKey === "system_email") {
      return contact?.email || lead.contact?.email || "";
    }
    if (fieldKey === "system_phone") {
      return contact?.phone || lead.contact?.phone || "";
    }

    // 2. Common Fields (Company)
    if (fieldKey === "company" || field.label?.toLowerCase() === "company") {
      return contact?.company || lead.contact?.company || "";
    }

    // 3. Status
    if (fieldKey === "status") {
      if (typeof lead.status === "object" && lead.status !== null) {
        return lead.status.name || "";
      }
      return lead.status || "";
    }

    // 4. Source
    if (fieldKey === "source") {
      return lead.source || "";
    }

    // 5. Custom Fields - Try top-level flat first, then nested
    if (lead[fieldKey] !== undefined && lead[fieldKey] !== null) {
      // Format number/currency if needed
      if (field.type === "number" || fieldKey === "budget") {
        const val = lead[fieldKey];
        return typeof val === "number" ? val.toLocaleString() : val;
      }
      return String(lead[fieldKey]);
    }

    // Fallback to nested customFields
    if (lead.customFields) {
      if (lead.customFields instanceof Map) {
        const val =
          lead.customFields.get(fieldKey) ?? lead.customFields.get(fieldId);
        if (val !== undefined && val !== null) return String(val);
      } else {
        const val = lead.customFields[fieldKey] ?? lead.customFields[fieldId];
        if (val !== undefined && val !== null) return String(val);
      }
    }
    return "";
  };

  return (
    <div className="space-y-4">
      {/* AI Suggestions Section - Show First */}
      {/* <div className=" hidden lg:grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
              ✨
            </span>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Live Conversation Coach
              </h3>
              <p className="text-xs text-slate-500">
                Sentiment:{" "}
                {safeAiSuggestions?.conversationCoach?.sentiment || "Neutral"}
              </p>
            </div>
          </div>
          <BulletList
            items={safeAiSuggestions?.conversationCoach?.recommendations || []}
          />
          <div className="mt-4 flex gap-3 flex-wrap">
            <button className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:border-slate-300">
              Insert ROI Template
            </button>
            <button className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:border-slate-300">
              Show Pricing
            </button>
          </div>
        </SectionCard>
        <SectionCard>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
              💡
            </span>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Deal Stage Guidance
              </h3>
              <p className="text-xs text-slate-500">
                Current Stage:{" "}
                {safeAiSuggestions?.dealStage?.stage || "Initial Contact"}
              </p>
            </div>
          </div>
          <BulletList items={safeAiSuggestions?.dealStage?.steps || []} />
          <div className="mt-4 flex gap-3 flex-wrap">
            <button className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:border-slate-300">
              Competitor Comparison
            </button>
            <button className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:border-slate-300">
              Book Demo
            </button>
          </div>
        </SectionCard>
      </div> */}

      {/* Lead Overview Section - Show Second */}
      <SectionCard>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="lg:text-xl font-semibold text-slate-900">
              Lead Overview
            </h2>
            <p className=" text-[13px] md:text-sm text-slate-500">
              Contact and lead information. Click edit to modify.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            {/* Status Dropdown / Badge Row */}
            <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
              {statuses && statuses.length > 0 ? (
                <StatusDropdown
                  status={lead.status || leadDetails?.status}
                  statuses={statuses}
                  onStatusChange={handleStatusChange}
                />
              ) : (
                <LeadStatusBadge status={lead.status || leadDetails?.status} />
              )}

              {/* Lead Score Badge */}
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ring-1 ${(lead.score ?? 0) >= 76 ? 'bg-emerald-50 ring-emerald-200 text-emerald-700' :
                (lead.score ?? 0) >= 51 ? 'bg-blue-50 ring-blue-200 text-blue-700' :
                  (lead.score ?? 0) >= 26 ? 'bg-amber-50 ring-amber-200 text-amber-700' :
                    'bg-red-50 ring-red-200 text-red-600'
                }`}>
                <div className={`w-2 h-2 rounded-full ${(lead.score ?? 0) >= 76 ? 'bg-emerald-500' :
                  (lead.score ?? 0) >= 51 ? 'bg-blue-500' :
                    (lead.score ?? 0) >= 26 ? 'bg-amber-500' :
                      'bg-red-500'
                  }`} />
                <span className="text-xs font-bold">Score: {lead.score ?? 0}</span>
              </div>

              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[#3f8cff] hover:text-[#3f8cff] transition-colors"
                >
                  <FiEdit2 size={16} />
                  Edit
                </button>
              )}
            </div>

            {/* Edit Actions Row - Stacks on mobile */}
            {isEditing && (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={handleCancel}
                  disabled={isUpdating}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:py-2 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  <FiX size={16} />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isUpdating}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:py-2 rounded-full bg-[#3f8cff] text-white text-sm font-semibold hover:bg-[#2f6bff] shadow-sm transition-colors disabled:opacity-50"
                >
                  <FiSave size={16} />
                  {isUpdating ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-4">
            {isLoadingFormConfig || isLoadingStatuses ? (
              <div className="text-center py-8 text-sm text-slate-500">
                Loading form...
              </div>
            ) : (
              <>
                {errors.submit && (
                  <div className="p-3 rounded-2xl bg-red-50 border border-red-200">
                    <p className="text-sm text-red-600">{errors.submit}</p>
                  </div>
                )}
                {formFields.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-500">
                    No form fields configured. Please configure the form in
                    settings.
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 mb-4">
                      Edit the fields below to update this lead's information.
                    </p>
                    {Object.keys(formValues).length === 0 &&
                      formFields.length > 0 ? (
                      <div className="text-center py-4 text-sm text-slate-500">
                        Initializing form...
                      </div>
                    ) : (
                      <DynamicLeadForm
                        fields={formFields}
                        values={formValues}
                        onChange={handleFormValueChange}
                        errors={errors}
                        statuses={statuses}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {formFields.map((field) => {
              const value = getFieldValue(field);

              // Format value based on field type
              let displayValue = value;
              if (field.type === "checkbox") {
                displayValue = value ? "Yes" : "No";
              } else if (field.type === "number" && value) {
                displayValue = Number(value).toLocaleString();
              } else if (field.type === "date" && value) {
                // Format date fields
                try {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                    displayValue = date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                  }
                } catch (e) {
                  displayValue = value;
                }
              } else if (field.type === "select" && value) {
                // For select fields, try to get the label from options
                const option = field.options?.find(
                  (opt) => opt === value || opt.value === value || opt.id === value || opt.name === value
                );
                if (option) {
                  displayValue = typeof option === "object" ? (option.label || option.name || option.id) : option;
                } else {
                  displayValue = value;
                }
              }

              // Show all fields (don't filter out empty ones)
              // Special handling for status field - show badge
              if (field.id === "status") {
                return (
                  <div key={field.id}>
                    <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                      {field.label}
                    </p>
                    <LeadStatusBadge
                      status={lead.status || leadDetails?.status}
                    />
                  </div>
                );
              }

              const leadStatus = lead.status?.name || lead.status || "";
              return (
                <LabelValue
                  key={field.id}
                  label={field.label}
                  value={displayValue || "—"}
                  valueClassName={(field.type === "date" || field.label?.toLowerCase().includes("date") || field.label?.toLowerCase().includes("deadline")) ? getDueDateColor(value, leadStatus) : ""}
                />
              );
            })}

            {/* Show additional fields that might not be in form config */}
            <LabelValue 
              label="Branch" 
              value={lead.branch || (lead.customFields instanceof Map ? lead.customFields.get("branch") : lead.customFields?.branch) || "Unknown"} 
            />
            {(() => {
              // "Lead Date" = actual date the lead arrived (fb_created_time takes priority)
              const fbCreatedTime = lead.fb_created_time || 
                (lead.customFields instanceof Map ? lead.customFields.get("fb_created_time") : lead.customFields?.fb_created_time);
              
              const formatD = (d) => {
                try {
                  const date = new Date(d);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
                  }
                } catch (e) {}
                return String(d);
              };

              const leadDate = fbCreatedTime ? formatD(fbCreatedTime) : (lead.createdAt ? formatD(lead.createdAt) : null);
              const crmDate = lead.createdAt ? formatD(lead.createdAt) : null;
              const isDifferent = fbCreatedTime && crmDate && leadDate !== crmDate;

              return (
                <>
                  {leadDate && (
                    <LabelValue
                      label="Lead Date"
                      value={leadDate}
                      valueClassName={isDifferent ? "text-amber-600 font-semibold" : "text-slate-900"}
                    />
                  )}
                  {crmDate && isDifferent && (
                    <LabelValue
                      label="CRM Created"
                      value={crmDate}
                    />
                  )}
                </>
              );
            })()}
            {leadDetails?.salesPerson && (
              <LabelValue label="Sales Person" value={leadDetails.salesPerson} />
            )}
            {lead.scheduled && (
              <LabelValue 
                label="Next Follow Up" 
                value={new Date(lead.scheduled).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit"
                })} 
                valueClassName="text-[#3f8cff] font-semibold"
              />
            )}
            {leadDetails?.campaignName && (
              <LabelValue label="Campaign" value={leadDetails.campaignName} />
            )}
            {leadDetails?.campaignSource && (
              <LabelValue label="Campaign Source" value={leadDetails.campaignSource} />
            )}
            {leadDetails?.campaignProject && (
              <LabelValue label="Campaign Project" value={leadDetails.campaignProject} />
            )}
          </div>
        )}
      </SectionCard>

      {/* Facebook Form Data Section - Show only for Facebook Leads */}
      {(lead.facebookLeadId || lead.source?.toLowerCase().includes("facebook")) && (
        <SectionCard>
          {(() => {
            const isWhatsApp = lead.platform?.toLowerCase() === "whatsapp" || lead.source?.toLowerCase() === "whatsapp";
            return (
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                  {isWhatsApp ? (
                    <span className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-[#25D366]">
                      <FaWhatsapp className="w-5 h-5" />
                    </span>
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-[#1877F2]">
                      <svg fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </span>
                  )}
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      {isWhatsApp ? "WhatsApp Lead Form Data" : "Facebook Lead Data"}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {isWhatsApp ? "All fields captured from the WhatsApp lead ad form" : "All fields captured from the Facebook form"}
                    </p>
                  </div>
                </div>
                {!isWhatsApp && lead.facebookLeadId && (
                  <button 
                    onClick={async () => {
                      let t;
                      try {
                        t = toast.loading("Syncing with Facebook...");
                        const res = await apiClient.post(`/leads/${lead._id}/sync-facebook`);
                        if (res.data?.success) {
                          toast.dismiss(t);
                          toast.success(res.data.message || "Synced successfully");
                          queryClient.invalidateQueries(["lead", lead.id || lead._id]);
                        } else {
                          toast.dismiss(t);
                          toast.error(res.data?.message || "Failed to sync");
                        }
                      } catch (error) {
                        toast.dismiss(t);
                        toast.error(error.response?.data?.message || "Error syncing with Facebook");
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sync with FB
                  </button>
                )}
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-4">
            {(() => {
              // 1. Gather standard fields that came from Facebook
              const standardFbData = [
                { key: "fb_lead_id", val: lead.facebookLeadId },
                { key: "fb_name", val: contact?.name || lead.contact?.name },
                { key: "fb_email", val: contact?.email || lead.contact?.email },
                { key: "fb_phone", val: contact?.phone || lead.contact?.phone },
                { key: "fb_platform", val: lead.platform }
              ].filter(item => item.val && String(item.val).trim() !== "");

              // 2. Gather all keys from customFields
              let customKeys = [];
              if (lead.customFields) {
                if (lead.customFields instanceof Map) {
                  customKeys = Array.from(lead.customFields.keys());
                } else {
                  customKeys = Object.keys(lead.customFields);
                }
              }

              // Also check top-level keys that might be from FB
              const fbKeys = ["fb_ad_id", "fb_ad_name", "fb_form_id", "fb_created_time"];
              const allPossibleKeys = [...new Set([...customKeys, ...fbKeys])];

              // Filter out empty metadata
              const unmappedKeys = allPossibleKeys.filter(key => {
                let val = "";
                if (fbKeys.includes(key)) {
                  val = lead[key] || (lead.customFields instanceof Map ? lead.customFields.get(key) : lead.customFields?.[key]);
                } else {
                  val = lead.customFields instanceof Map ? lead.customFields.get(key) : lead.customFields?.[key];
                }
                return val !== undefined && val !== null && String(val).trim() !== "";
              });

              if (unmappedKeys.length === 0 && standardFbData.length === 0) {
                return <p className="col-span-full text-sm text-slate-400 italic">No Facebook form data found.</p>;
              }

              // Combine both sets of data
              const allDataToRender = [
                ...standardFbData,
                ...unmappedKeys.map(key => {
                  let val = "";
                  if (fbKeys.includes(key)) {
                    val = lead[key] || (lead.customFields instanceof Map ? lead.customFields.get(key) : lead.customFields?.[key]);
                  } else {
                    val = lead.customFields instanceof Map ? lead.customFields.get(key) : lead.customFields?.[key];
                  }
                  return { key, val };
                })
              ];

              return allDataToRender.map(({ key, val }) => {

                return (
                  <div key={key} className="relative group">
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#3f8cff]/30 transition-all">
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                          {key.replace("fb_", "").replace(/_/g, " ")}
                        </p>
                        <button
                          onClick={() => setMappingModal({
                            isOpen: true,
                            facebookField: key,
                            facebookValue: val
                          })}
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-[10px] font-bold text-[#3f8cff] hover:underline transition-all"
                        >
                          <FiLink size={10} />
                          MAP FIELD
                        </button>
                      </div>
                      <p className="text-sm font-semibold text-slate-700 break-words">
                        {(key === "fb_created_time" || key === "created_time") && !isNaN(new Date(String(val)).getTime())
                          ? new Date(String(val)).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : String(val)}
                      </p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </SectionCard>
      )}

      {/* Mapping Modal */}
      <LeadFieldMapModal
        isOpen={mappingModal.isOpen}
        onClose={() => setMappingModal({ ...mappingModal, isOpen: false })}
        leadId={lead.id || lead._id}
        facebookField={mappingModal.facebookField}
        facebookValue={mappingModal.facebookValue}
        crmFields={formFields}
        branches={branches}
        onSuccess={() => {
          // You might want to refresh lead data here
          // For now toast is enough
        }}
      />

      {/* Quick Add Actions - Explicitly for Notes & Attachments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <SectionCard>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              📝 Add Quick Note
            </h3>
          </div>
          <textarea
            value={quickNoteText}
            onChange={(e) => setQuickNoteText(e.target.value)}
            placeholder="Type your note here..."
            rows={3}
            className="w-full p-3 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3f8cff] focus:border-transparent resize-none bg-slate-50/50"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleSaveQuickNote}
              disabled={!quickNoteText.trim() || isAddingNote}
              className="px-4 py-2 rounded-xl bg-[#3f8cff] text-white text-xs font-semibold hover:bg-[#2f6bff] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
            >
              {isAddingNote ? "Adding..." : "Add Note"}
            </button>
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              📎 Upload Attachment
            </h3>
          </div>
          <div className="flex flex-col h-full justify-between">
            <label className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#3f8cff] hover:bg-slate-50/50 transition-all flex flex-col items-center justify-center gap-2">
              <input
                type="file"
                className="hidden"
                onChange={handleQuickFileUpload}
                disabled={isUploadingFile}
              />
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4-4m4-4v12" />
              </svg>
              <span className="text-xs font-semibold text-slate-600">
                {isUploadingFile ? "Uploading..." : "Select or Drop a file"}
              </span>
            </label>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default LeadOverviewSection;
